import type { JSX } from 'react'
import type {
  ConditionTree,
  ConditionBranch,
  ConditionLeaf,
  Operator,
} from '@kc3/conversations'
import Select from '../../ui/Select.js'

/**
 * A field descriptor in the RuleBuilder field picker. Mirrors what
 * `useRuleFields(domain)` returns from GET /admin/conversations/rule-fields,
 * with an optional `operators` override.
 */
export interface FieldDef {
  key: string
  label: string
  type: 'string' | 'number' | 'boolean' | 'enum' | 'array'
  enumValues?: string[]
  operators?: Operator[]
}

export interface RuleBuilderProps {
  value: ConditionTree
  onChange: (next: ConditionTree) => void
  fields: FieldDef[]
  /** Maximum nesting depth (default 5). At max depth, "+ Add group" is hidden. */
  maxDepth?: number
}

/**
 * Operator-by-type allowlist (plan §Task 5). Used to filter the operator
 * dropdown unless the FieldDef provides its own `operators` override.
 */
export const OPERATORS_BY_TYPE: Record<FieldDef['type'], Operator[]> = {
  string: ['eq', 'neq', 'contains', 'is_null', 'is_not_null'],
  number: ['eq', 'neq', 'gt', 'lt', 'gte', 'lte', 'is_null', 'is_not_null'],
  boolean: ['eq', 'neq', 'is_null', 'is_not_null'],
  enum: ['eq', 'neq', 'in', 'not_in', 'is_null', 'is_not_null'],
  array: ['contains', 'in', 'not_in', 'is_null', 'is_not_null'],
}

const OPERATOR_LABELS: Record<Operator, string> = {
  eq: 'equals',
  neq: 'not equals',
  in: 'in',
  not_in: 'not in',
  contains: 'contains',
  not_contains: 'does not contain',
  begins_with: 'begins with',
  ends_with: 'ends with',
  between: 'is between',
  gt: '>',
  lt: '<',
  gte: '>=',
  lte: '<=',
  is_null: 'is empty',
  is_not_null: 'is not empty',
}

function isBranch(tree: ConditionTree): tree is ConditionBranch {
  return (
    (tree as ConditionBranch).operator !== undefined &&
    Array.isArray((tree as ConditionBranch).conditions)
  )
}

function isLeaf(tree: ConditionTree): tree is ConditionLeaf {
  return typeof (tree as ConditionLeaf).field === 'string'
}

function isEmpty(tree: ConditionTree): boolean {
  return !isBranch(tree) && !isLeaf(tree)
}

function defaultLeafForField(field: FieldDef): ConditionLeaf {
  const ops = field.operators ?? OPERATORS_BY_TYPE[field.type]
  const op = ops[0] ?? 'eq'
  let value: unknown = ''
  if (field.type === 'number') value = 0
  else if (field.type === 'boolean') value = false
  else if (field.type === 'array') value = []
  else if (field.type === 'enum') value = field.enumValues?.[0] ?? ''
  return { field: field.key, op, value }
}

function newLeaf(fields: FieldDef[]): ConditionLeaf {
  const first = fields[0]
  if (first === undefined) {
    return { field: '', op: 'eq', value: '' }
  }
  return defaultLeafForField(first)
}

function newGroup(fields: FieldDef[]): ConditionBranch {
  return { operator: 'AND', conditions: [newLeaf(fields)] }
}

function valueSummary(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.join(',')
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  return String(value)
}

/**
 * Visual editor for an AND/OR condition tree. Recursive: branches render a
 * group container with an operator toggle + child nodes; leaves render a
 * field/operator/value row. Pure & controlled — all state lives in `value`
 * and changes flow through `onChange` with an immutable next tree.
 *
 * Shared with UI-2b/2c/2d/2e (routing, automation, escalation, custom
 * fields). Knows nothing about API calls or global state.
 */
export default function RuleBuilder(props: RuleBuilderProps): JSX.Element {
  const { value, onChange, fields, maxDepth = 5 } = props

  // Empty tree → empty state with "+ Add condition".
  if (isEmpty(value)) {
    return (
      <div className="rb-empty rounded border border-dashed border-gray-300 p-4">
        <p className="mb-2 text-sm text-gray-600">No conditions yet.</p>
        <button
          type="button"
          className="rounded border border-gray-300 px-2 py-1 text-xs"
          onClick={() => onChange(newLeaf(fields))}
        >
          + Add condition
        </button>
      </div>
    )
  }

  // Top-level leaf → wrap with a synthetic group so the user can add siblings.
  if (isLeaf(value)) {
    const wrapped: ConditionBranch = { operator: 'AND', conditions: [value] }
    return (
      <RuleBuilder
        {...props}
        value={wrapped}
        onChange={(next) => {
          // Unwrap if the user reduced back to a single leaf? Keep as branch
          // for stability — engines accept both shapes.
          onChange(next)
        }}
      />
    )
  }

  // Branch — render group.
  const branchValue = value as ConditionBranch
  return (
    <BranchNode
      branch={branchValue}
      onChange={onChange}
      fields={fields}
      maxDepth={maxDepth}
      depth={1}
      onRemove={null}
    />
  )
}

interface BranchNodeProps {
  branch: ConditionBranch
  onChange: (next: ConditionTree) => void
  fields: FieldDef[]
  maxDepth: number
  depth: number
  /** When non-null, render a "Remove group" button that calls this. */
  onRemove: (() => void) | null
}

function BranchNode({
  branch,
  onChange,
  fields,
  maxDepth,
  depth,
  onRemove,
}: BranchNodeProps): JSX.Element {
  function updateConditionAt(index: number, next: ConditionTree): void {
    const nextConditions = branch.conditions.map((c: ConditionTree, i: number) => (i === index ? next : c))
    onChange({ operator: branch.operator, conditions: nextConditions })
  }

  function removeConditionAt(index: number): void {
    const nextConditions = branch.conditions.filter((_: ConditionTree, i: number) => i !== index)
    onChange({ operator: branch.operator, conditions: nextConditions })
  }

  function setOperator(op: 'AND' | 'OR'): void {
    onChange({ operator: op, conditions: [...branch.conditions] })
  }

  function addCondition(): void {
    onChange({
      operator: branch.operator,
      conditions: [...branch.conditions, newLeaf(fields)],
    })
  }

  function addGroup(): void {
    onChange({
      operator: branch.operator,
      conditions: [...branch.conditions, newGroup(fields)],
    })
  }

  // A new nested group at this level would sit at depth (depth+1); its
  // first leaf child at depth+2 must be <= maxDepth. So we require
  // depth+2 <= maxDepth. (Mirrors conditionTreeDepth: branch contributes
  // 1 + max child depth, and an "empty" branch wouldn't be created — we
  // always seed with at least one leaf.)
  const canAddGroup = depth + 2 <= maxDepth

  const opLabel = branch.operator === 'AND' ? 'AND' : 'OR'

  return (
    <div
      role="group"
      aria-label={`Condition group (${opLabel})`}
      className="rb-group rounded border border-gray-300 p-3"
    >
      <div className="mb-2 flex items-center gap-2">
        <span className="text-xs uppercase text-gray-500">Match</span>
                <Select
          ariaLabel="Group operator"
          size="sm"
          value={branch.operator}
          onChange={(v) => setOperator(v === 'OR' ? 'OR' : 'AND')}
        >
          <option value="AND">ALL (AND)</option>
          <option value="OR">ANY (OR)</option>
                </Select>
        {onRemove !== null && (
          <button
            type="button"
            aria-label="Remove group"
            className="ml-auto rounded border border-gray-300 px-2 py-0.5 text-xs"
            onClick={onRemove}
          >
            Remove group
          </button>
        )}
      </div>

      <div className="flex flex-col gap-2">
        {branch.conditions.map((child: ConditionTree, i: number) => {
          if (isBranch(child)) {
            return (
              <BranchNode
                key={i}
                branch={child}
                onChange={(next) => updateConditionAt(i, next)}
                fields={fields}
                maxDepth={maxDepth}
                depth={depth + 1}
                onRemove={() => removeConditionAt(i)}
              />
            )
          }
          // Leaf (or empty — treat as leaf shell)
          const leaf: ConditionLeaf = isLeaf(child)
            ? child
            : { field: '', op: 'eq', value: '' }
          return (
            <LeafRow
              key={i}
              leaf={leaf}
              fields={fields}
              onChange={(next) => updateConditionAt(i, next)}
              onRemove={() => removeConditionAt(i)}
            />
          )
        })}
      </div>

      <div className="mt-2 flex gap-2">
        <button
          type="button"
          className="rounded border border-gray-300 px-2 py-1 text-xs"
          onClick={addCondition}
        >
          + Add condition
        </button>
        {canAddGroup && (
          <button
            type="button"
            className="rounded border border-gray-300 px-2 py-1 text-xs"
            onClick={addGroup}
          >
            + Add group
          </button>
        )}
      </div>
    </div>
  )
}

interface LeafRowProps {
  leaf: ConditionLeaf
  fields: FieldDef[]
  onChange: (next: ConditionLeaf) => void
  onRemove: () => void
}

function LeafRow({ leaf, fields, onChange, onRemove }: LeafRowProps): JSX.Element {
  const field = fields.find((f) => f.key === leaf.field)
  const fieldLabel = field?.label ?? leaf.field
  const fieldType: FieldDef['type'] = field?.type ?? 'string'
  const allowedOps: Operator[] =
    field?.operators ?? OPERATORS_BY_TYPE[fieldType]
  const opLabel = OPERATOR_LABELS[leaf.op] ?? leaf.op
  const ariaLabel = `Condition: ${fieldLabel} ${opLabel} ${valueSummary(leaf.value)}`.trim()
  const hideValue = leaf.op === 'is_null' || leaf.op === 'is_not_null'

  function changeField(nextKey: string): void {
    const nextField = fields.find((f) => f.key === nextKey)
    if (nextField === undefined) {
      onChange({ ...leaf, field: nextKey })
      return
    }
    const nextDefault = defaultLeafForField(nextField)
    // Preserve op when it's still valid; otherwise reset.
    const nextOps = nextField.operators ?? OPERATORS_BY_TYPE[nextField.type]
    const keepOp = nextOps.includes(leaf.op) ? leaf.op : nextDefault.op
    onChange({ field: nextField.key, op: keepOp, value: nextDefault.value })
  }

  function changeOp(nextOp: Operator): void {
    onChange({ ...leaf, op: nextOp })
  }

  function changeValue(next: unknown): void {
    onChange({ ...leaf, value: next })
  }

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="rb-leaf flex items-center gap-2"
    >
            <Select
        ariaLabel="Field"
        size="sm"
        value={leaf.field}
        onChange={(v) => changeField(v)}
      >
        {fields.map((f) => (
          <option key={f.key} value={f.key}>
            {f.label}
          </option>
        ))}
        {field === undefined && leaf.field !== '' && (
          <option value={leaf.field}>{leaf.field}</option>
        )}
            </Select>

            <Select
        ariaLabel="Operator"
        size="sm"
        value={leaf.op}
        onChange={(v) => changeOp(v as Operator)}
      >
        {allowedOps.map((op) => (
          <option key={op} value={op}>
            {OPERATOR_LABELS[op]}
          </option>
        ))}
            </Select>

      {!hideValue && (
        <LeafValueInput
          fieldType={fieldType}
          enumValues={field?.enumValues}
          op={leaf.op}
          value={leaf.value}
          onChange={changeValue}
        />
      )}

      <button
        type="button"
        aria-label="Remove condition"
        className="ml-auto rounded border border-gray-300 px-2 py-0.5 text-xs"
        onClick={onRemove}
      >
        Remove
      </button>
    </div>
  )
}

interface LeafValueInputProps {
  fieldType: FieldDef['type']
  enumValues?: string[] | undefined
  op: Operator
  value: unknown
  onChange: (next: unknown) => void
}

function LeafValueInput({
  fieldType,
  enumValues,
  op,
  value,
  onChange,
}: LeafValueInputProps): JSX.Element {
  // `in`/`not_in` always means a multi-value array regardless of base type.
  const multi = op === 'in' || op === 'not_in' || fieldType === 'array'

  if (multi) {
    const arr = Array.isArray(value) ? value : []
    const textValue = arr.join(',')
    return (
      <input
        type="text"
        aria-label="Value"
        placeholder="comma,separated,values"
        value={textValue}
        onChange={(e) => {
          const next = e.target.value
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s.length > 0)
          onChange(next)
        }}
        className="flex-1 rounded border border-gray-300 px-2 py-0.5 text-xs"
      />
    )
  }

  if (fieldType === 'number') {
    return (
      <input
        type="number"
        aria-label="Value"
        value={typeof value === 'number' ? value : ''}
        onChange={(e) => {
          const n = e.target.value === '' ? 0 : Number(e.target.value)
          onChange(Number.isNaN(n) ? 0 : n)
        }}
        className="w-32 rounded border border-gray-300 px-2 py-0.5 text-xs"
      />
    )
  }

  if (fieldType === 'boolean') {
    return (
      <input
        type="checkbox"
        aria-label="Value"
        checked={value === true}
        onChange={(e) => onChange(e.target.checked)}
      />
    )
  }

  if (fieldType === 'enum') {
    const opts = enumValues ?? []
    return (
            <Select
        ariaLabel="Value"
        size="sm"
        value={typeof value === 'string' ? value : ''}
        onChange={(v) => onChange(v)}
      >
        {opts.map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
            </Select>
    )
  }

  // string default
  return (
    <input
      type="text"
      aria-label="Value"
      value={typeof value === 'string' ? value : ''}
      onChange={(e) => onChange(e.target.value)}
      className="flex-1 rounded border border-gray-300 px-2 py-0.5 text-xs"
    />
  )
}
